/**
 * Routing wire types — Document Management's Receiving, Dispatching and Inbox, and the route they share.
 *
 * **No person's NAME appears on any response.** The sender and the addressee travel as a
 * `PersonId`; the profile asks `personsApi.resolve` for the one on screen. A REQUEST carries the
 * four name parts, which the server writes to the identity store and never keeps here.
 *
 * **Every enum is NUMERIC on the wire** (no string-enum converter is configured), so each is an
 * `as const` object of numbers, with the server's label beside it on the DTO. Facet values are
 * the exception and stay the enum NAMES.
 */

export const DOCUMENT_MEDIUM = { hardCopy: 0, softCopy: 1 } as const;

export type DocumentMedium = (typeof DOCUMENT_MEDIUM)[keyof typeof DOCUMENT_MEDIUM];

/** Mirrors `RoutingLabels.Of(DocumentMedium)` — a DISPATCH's medium; a received document is always a hard copy. */
export const MEDIUM_LABEL: Record<DocumentMedium, string> = {
  0: 'Hard copy',
  1: 'Soft copy',
};

export const ROUTING_STATUS = { received: 0, inTransit: 1, dispatched: 2 } as const;

export type RoutingStatus = (typeof ROUTING_STATUS)[keyof typeof ROUTING_STATUS];

export const ROUTE_KIND = { received: 0, forwarded: 1, dispatched: 2 } as const;

export type DocumentRouteKind = (typeof ROUTE_KIND)[keyof typeof ROUTE_KIND];

export const ROUTE_PURPOSE = {
  forAction: 0,
  forInformation: 1,
  forSignature: 2,
  forComment: 3,
  forFiling: 4,
} as const;

export type RoutePurpose = (typeof ROUTE_PURPOSE)[keyof typeof ROUTE_PURPOSE];

/** Mirrors `RoutingLabels.Of(RoutePurpose)`. */
export const PURPOSE_LABEL: Record<RoutePurpose, string> = {
  0: 'For action',
  1: 'For information',
  2: 'For signature',
  3: 'For comment',
  4: 'For filing',
};

export const DISPATCH_MODE = { handCarried: 0, courier: 1, mail: 2, email: 3 } as const;

export type DispatchMode = (typeof DISPATCH_MODE)[keyof typeof DISPATCH_MODE];

/** Mirrors `RoutingLabels.Of(DispatchMode)`. */
export const MODE_LABEL: Record<DispatchMode, string> = {
  0: 'Hand-carried',
  1: 'Courier',
  2: 'Mail',
  3: 'Email',
};

/**
 * Every entity type each register writes — what its History Logs filter on. A route line and a
 * filed file are children the parent's history would otherwise be blind to.
 */
export const RECEIVING_AUDIT_TYPES = ['ReceivedDocument', 'DocumentRoute', 'RoutingAttachment'];

export const DISPATCHING_AUDIT_TYPES = ['DispatchedDocument', 'DocumentRoute', 'RoutingAttachment'];

/** The Inbox writes a dispatch's route lines and the files a forward carries — receiving one stamps its line. */
export const INBOX_AUDIT_TYPES = ['DocumentRoute', 'RoutingAttachment'];

// ── Document types ────────────────────────────────────────────────────────────────────────

export interface RoutingDocumentTypeListItem {
  id: string;
  name: string;
  description: string | null;
  receivedCount: number;
  dispatchedCount: number;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  isActive: boolean;
}

export interface RoutingDocumentTypeOption {
  id: string;
  name: string;
}

export interface RoutingDocumentTypeRequest {
  name: string;
  description: string | null;
}

// ── Shared ────────────────────────────────────────────────────────────────────────────────

export interface RoutingUnitOption {
  id: string;
  name: string;
  code: string | null;
}

export interface DocumentRouteItem {
  id: string;
  sequence: number;
  kind: DocumentRouteKind;
  kindLabel: string;
  receivedDocumentId: string | null;
  receivedTrackingNumber: string | null;
  dispatchedDocumentId: string | null;
  dispatchedTrackingNumber: string | null;
  fromOrgUnitId: string | null;
  fromOrgUnitName: string | null;
  toOrgUnitId: string | null;
  toOrgUnitName: string | null;
  externalOffice: string | null;
  purpose: RoutePurpose | null;
  purposeLabel: string | null;
  remarks: string | null;
  routedAt: string;
  acknowledgedAt: string | null;
  acknowledgedByName: string | null;
  /** The employee a forward is addressed to — a PersonnelId, resolved through the personnel record. */
  toPersonnelId: string | null;
  /** Files the sending unit attached to this forward. */
  attachmentCount: number;
  /** Per-caller: the named employee, or anyone posted to the unit it went to. Never the sender. */
  canAcknowledge: boolean;
  /** Per-caller: anyone posted to the unit that sent it, while it is open. */
  canCancel: boolean;
  /** Per-caller: the sending unit, while it is open. */
  canAttach: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  isActive: boolean;
}

/**
 * Where the CALLER sits for routing — their employee record and post's unit. All null when the
 * account is linked to no employee with a post, and then it routes, acknowledges and cancels nothing.
 */
export interface RoutingDesk {
  personnelId: string | null;
  orgUnitId: string | null;
  orgUnitName: string | null;
}

export interface RoutingAttachmentItem {
  id: string;
  fileName: string;
  /** The VERIFIED media type — what the preview pins its blob to. */
  contentType: string;
  sizeBytes: number;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  isActive: boolean;
}

/** The four name parts a request carries. Never on a response. */
export interface PersonNameParts {
  /** A TITLE — Mr., Dr., Atty. Optional; see `lib/salutations.ts`. */
  salutation: string;
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
}

// ── Receiving ─────────────────────────────────────────────────────────────────────────────

/**
 * A received document is a HARD COPY. Its sender is a unit of this organization
 * (`senderOrgUnitId`) or an outside organization typed in (`senderOffice`) — exactly one. Only an
 * outside sender may carry a named person.
 */
export interface ReceivedDocumentListItem {
  id: string;
  trackingNumber: string;
  subject: string;
  documentTypeId: string;
  documentTypeName: string;
  senderOrgUnitId: string | null;
  senderOrgUnitName: string | null;
  senderOffice: string | null;
  receivedAt: string;
  receivingOrgUnitId: string;
  receivingOrgUnitName: string;
  /** Every unit it is with now, in the order they came to hold it — several once routed to more than one. */
  holders: RoutingUnitOption[];
  status: RoutingStatus;
  statusLabel: string;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  isActive: boolean;
}

export interface ReceivedDocumentDetail extends ReceivedDocumentListItem {
  senderPersonId: string | null;
  senderReferenceNumber: string | null;
  documentDate: string | null;
  remarks: string | null;
  /** Every forward still open — there may be several. */
  pendingRoutes: DocumentRouteItem[];
  /** Per-caller: whether this account may route it on now, and why not. */
  canRoute: boolean;
  routeBlockedReason: string | null;
  dispatchedDocumentId: string | null;
  dispatchedTrackingNumber: string | null;
  dispatchedTo: string | null;
  routeCount: number;
  canChangeReceivingUnit: boolean;
}

export interface ReceivedDocumentListQuery {
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  status?: string;
  routing?: string;
  documentType?: string;
  /** `internal` or `external` — the facet's two values. */
  sender?: string;
  holder?: string;
}

export interface ReceivedDocumentRequest {
  subject: string;
  documentTypeId: string | null;
  senderOrgUnitId: string | null;
  senderOffice: string | null;
  senderSalutation: string | null;
  senderFirstName: string | null;
  senderMiddleName: string | null;
  senderLastName: string | null;
  senderSuffix: string | null;
  senderReferenceNumber: string | null;
  documentDate: string | null;
  receivedAt: string | null;
  receivingOrgUnitId: string | null;
  remarks: string | null;
}

export interface RouteDocumentRequest {
  toOrgUnitId: string | null;
  /** Optional — the employee within that unit it is addressed to. */
  toPersonnelId: string | null;
  purpose: RoutePurpose | null;
  remarks: string | null;
}

/** Which side of the organization a correspondent sits — the form's own choice, never on the wire. */
export type CorrespondentKind = 'internal' | 'external';

// ── Dispatching ───────────────────────────────────────────────────────────────────────────

/**
 * A dispatch goes to a unit of this organization (`recipientOrgUnitId` — it lands in that unit's
 * Inbox) or to an outside office (`recipientOffice`). Holder and status are the route's projection.
 */
export interface DispatchedDocumentListItem {
  id: string;
  trackingNumber: string;
  subject: string;
  documentTypeId: string;
  documentTypeName: string;
  medium: DocumentMedium;
  mediumLabel: string;
  recipientOrgUnitId: string | null;
  recipientOrgUnitName: string | null;
  recipientOffice: string | null;
  mode: DispatchMode;
  modeLabel: string;
  dispatchedAt: string;
  originatingOrgUnitId: string;
  originatingOrgUnitName: string;
  /** Every unit it is with now, in the order they came to hold it — several once routed to more than one. */
  holders: RoutingUnitOption[];
  status: RoutingStatus;
  statusLabel: string;
  receivedDocumentId: string | null;
  receivedTrackingNumber: string | null;
  /** When the recipient unit's Inbox took it in — null until then, and always for an outside office. */
  inboxReceivedAt: string | null;
  attachmentCount: number;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  isActive: boolean;
}

export interface DispatchedDocumentDetail extends DispatchedDocumentListItem {
  recipientPersonId: string | null;
  carrierReferenceNumber: string | null;
  pendingRoutes: DocumentRouteItem[];
  /** Per-caller, on the Inbox: whether this account may route it on now, and why not. */
  canRoute: boolean;
  routeBlockedReason: string | null;
  /** Per-caller: whether this account is posted to the unit it was sent to and it is still waiting. */
  canReceive: boolean;
  inboxReceivedByName: string | null;
  receivedSubject: string | null;
  remarks: string | null;
  routeCount: number;
}

export interface DispatchedDocumentListQuery {
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  status?: string;
  documentType?: string;
  medium?: string;
  mode?: string;
  origin?: string;
  source?: string;
  /** `internal` or `external`. */
  destination?: string;
}

export interface DispatchedDocumentRequest {
  receivedDocumentId: string | null;
  originatingOrgUnitId: string | null;
  subject: string;
  documentTypeId: string | null;
  medium: DocumentMedium | null;
  recipientOrgUnitId: string | null;
  recipientOffice: string | null;
  recipientSalutation: string | null;
  recipientFirstName: string | null;
  recipientMiddleName: string | null;
  recipientLastName: string | null;
  recipientSuffix: string | null;
  mode: DispatchMode | null;
  dispatchedAt: string | null;
  carrierReferenceNumber: string | null;
  remarks: string | null;
}

export interface DispatchableDocumentOption {
  id: string;
  trackingNumber: string;
  subject: string;
  documentTypeId: string;
  /** The sender as the register shows it — a unit's name or the outside organization. */
  sentBy: string;
  /** Every unit it is with now, in the order they came to hold it — several once routed to more than one. */
  holders: RoutingUnitOption[];
}

// ── Inbox ─────────────────────────────────────────────────────────────────────────────────

/** An Inbox item IS a dispatch addressed to a unit — the same row, read from the receiving side. */
export interface InboxListQuery {
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  status?: string;
  routing?: string;
  recipient?: string;
  holder?: string;
  documentType?: string;
}
